import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { PlusIcon, UserIcon, EditIcon, CheckIcon, XIcon, TrashIcon, AlertTriangleIcon, RefreshCwIcon, MessageCircleIcon, CrownIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagementSettings = () => {
  const [users, setUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [managers, setManagers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companyStats, setCompanyStats] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Country phone codes state (loaded from API)
  const [countryCodes, setCountryCodes] = useState([]);
  const [defaultCountryCode, setDefaultCountryCode] = useState('+90');
  
  // Invite user state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role_id: '', manager_id: 'none' });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [suggestedManagers, setSuggestedManagers] = useState([]);
  
  // Phone number state
  const [phoneCountryCode, setPhoneCountryCode] = useState('+90');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Edit user state
  const [editingUserId, setEditingUserId] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [updatingUser, setUpdatingUser] = useState(false);

  // Helper function to extract user data from different response formats
  const getUserData = (userItem) => {
    // Support both formats:
    // Format 1: {user: {...}, role: {...}, manager: {...}}
    // Format 2: {id: ..., email: ..., ...} (direct user object)
    return userItem?.user || userItem;
  };

  // Helper function to extract role data
  const getRoleData = (userItem) => {
    return userItem?.role || null;
  };

  // Generate employee ID
  const generateEmployeeId = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `EMP-${timestamp}-${random}`;
  };

  // Cancel invitation modal state
  const [cancelInviteDialogOpen, setCancelInviteDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);

  const [selectedInactiveUser, setSelectedInactiveUser] = useState(null);

  // Delete user modal state
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null);
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Archive user modal state
  const [archiveUserDialogOpen, setArchiveUserDialogOpen] = useState(false);
  const [selectedUserToArchive, setSelectedUserToArchive] = useState(null);
  const [archivingUser, setArchivingUser] = useState(false);

  // Create user with credentials state
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [createUserData, setCreateUserData] = useState({
    email: '',
    full_name: '',
    role_id: '',
    phone: '',
    position_id: null,
    department_id: null,
    manager_id: null,
    send_method: 'email'
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  
  // Create user phone state
  const [createPhoneCountryCode, setCreatePhoneCountryCode] = useState(defaultCountryCode);
  const [createPhoneNumber, setCreatePhoneNumber] = useState('');
  const [createPhoneError, setCreatePhoneError] = useState('');

  // User change requests state
  const [changeRequests, setChangeRequests] = useState([]);
  const [loadingChangeRequests, setLoadingChangeRequests] = useState(false);

  // Fetch country phone codes
  const fetchCountryCodes = async () => {
    try {
      const response = await axiosInstance.get('/country-phone-codes');
      setCountryCodes(response.data);
      
      // Find Turkey code as default
      const turkeyCode = response.data.find(c => c.code === '+90');
      if (turkeyCode) {
        setDefaultCountryCode(turkeyCode.code);
        setPhoneCountryCode(turkeyCode.code);
        setCreatePhoneCountryCode(turkeyCode.code);
      }
    } catch (error) {
      console.error('❌ Failed to load country codes:', error);
      toast.error('Ülke kodları yüklenemedi');
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/users`);
      
      // Handle different response formats:
      // Format 1: {success: true, users: [...]} (wrapped)
      // Format 2: [{user: {...}, role: {...}}] (direct array)
      let userData = response.data?.users || response.data || [];
      
      // Ensure userData is an array
      const usersArray = Array.isArray(userData) ? userData : [];
      
      console.log('🔍 Fetched users:', usersArray.length);
      console.log('🔍 First user data:', usersArray[0]);
      
      // Aktif ve pasif kullanıcıları ayır - check both possible formats
      const activeUsers = usersArray.filter(userItem => {
        const user = userItem?.user || userItem;  // Support both formats
        return user?.is_active === true;
      });
      
      const inactiveUsers = usersArray.filter(userItem => {
        const user = userItem?.user || userItem;  // Support both formats
        return user?.is_active === false;
      });
      
      console.log('👑 Super admin users:', activeUsers.filter(u => (u?.user || u)?.is_super_admin));
      console.log('✅ Active users:', activeUsers.length);
      console.log('❌ Inactive users:', inactiveUsers.length);
      console.log('📋 Inactive users data:', inactiveUsers);
      
      setUsers(activeUsers);
      setInactiveUsers(inactiveUsers);
    } catch (error) {
      console.error('❌ Load users error:', error);
      toast.error('Kullanıcılar yüklenirken hata oluştu: ' + (error.response?.data?.detail || error.message));
      setUsers([]);
      setInactiveUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get(`/roles`);
      setRoles(response.data);
    } catch (error) {
      toast.error('Roller yüklenirken hata oluştu');
    }
  };

  // Fetch managers
  const fetchManagers = async () => {
    try {
      const response = await axiosInstance.get(`/users/managers`);
      setManagers(response.data);
    } catch (error) {
      toast.error('Yöneticiler yüklenirken hata oluştu');
    }
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const response = await axiosInstance.get(`/departments/`);
      console.log('Departments loaded:', response.data);
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Departmanlar yüklenirken hata:', error);
      toast.error('Departmanlar yüklenemedi');
    }
  };

  // Validate phone number (for invite form)
  const validatePhoneNumber = () => {
    if (!phoneNumber) {
      setPhoneError('');
      return true;
    }
    
    // Only numbers allowed
    if (!/^\d+$/.test(phoneNumber)) {
      setPhoneError('Lütfen geçerli bir telefon numarası girin');
      return false;
    }
    
    // Max 10 digits
    if (phoneNumber.length > 10) {
      setPhoneError('Telefon numarası en fazla 10 rakam olabilir');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  // Handle phone number change (for invite form)
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      // Max 10 digits
      if (value.length <= 10) {
        setPhoneNumber(value);
        setPhoneError('');
      }
    }
  };

  // Validate phone number (for create user form)
  const validateCreatePhoneNumber = () => {
    if (!createPhoneNumber) {
      setCreatePhoneError('');
      return true;
    }
    
    // Only numbers allowed
    if (!/^\d+$/.test(createPhoneNumber)) {
      setCreatePhoneError('Lütfen geçerli bir telefon numarası girin');
      return false;
    }
    
    // Max 10 digits
    if (createPhoneNumber.length > 10) {
      setCreatePhoneError('Telefon numarası en fazla 10 rakam olabilir');
      return false;
    }
    
    setCreatePhoneError('');
    return true;
  };

  // Handle phone number change (for create user form)
  const handleCreatePhoneNumberChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      // Max 10 digits
      if (value.length <= 10) {
        setCreatePhoneNumber(value);
        setCreatePhoneError('');
      }
    }
  };

  // Toggle Super Admin status for a user
  const toggleSuperAdmin = async (user) => {
    const userData = getUserData(user);
    const currentStatus = userData.is_super_admin || false;
    const newStatus = !currentStatus;
    
    // Confirmation dialog
    const confirmMessage = newStatus
      ? `"${userData.full_name}" kullanıcısına TAM YETKİ (Süper Admin) vermek istediğinizden emin misiniz?\n\nBu kullanıcı:\n• Tüm modüllere erişim\n• Tüm kullanıcıları yönetme\n• Tüm ayarları değiştirme\n• Tüm raporları görüntüleme\nyetkisine sahip olacaktır.`
      : `"${userData.full_name}" kullanıcısının TAM YETKİSİNİ (Süper Admin) kaldırmak istediğinizden emin misiniz?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setUpdatingUser(true);
      const response = await axiosInstance.post(`/users/${userData.id}/toggle-super-admin`);
      
      toast.success(response.data.message || 'Kullanıcı yetkisi güncellendi');
      
      // Refresh user list
      await fetchUsers();
    } catch (error) {
      console.error('Error toggling super admin:', error);
      toast.error(error.response?.data?.detail || 'Yetki değiştirilirken hata oluştu');
    } finally {
      setUpdatingUser(false);
    }
  };

  // Fetch user change requests
  const fetchChangeRequests = async () => {
    try {
      setLoadingChangeRequests(true);
      const response = await axiosInstance.get(`/user-change-requests`);
      setChangeRequests(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Değişiklik talepleri yüklenirken hata oluştu');
      }
    } finally {
      setLoadingChangeRequests(false);
    }
  };

  // Fetch company stats
  const fetchCompanyStats = async () => {
    try {
      const response = await axiosInstance.get(`/company/stats`);
      setCompanyStats(response.data);
    } catch (error) {
      toast.error('Şirket bilgileri yüklenirken hata oluştu');
    }
  };

  // Fetch pending invitations
  const fetchPendingInvitations = async () => {
    try {
      const response = await axiosInstance.get(`/users/pending-invitations`);
      setPendingInvitations(response.data);
    } catch (error) {
      toast.error('Bekleyen davetler yüklenirken hata oluştu');
    }
  };

  // Fetch archived users
  const fetchArchivedUsers = async () => {
    try {
      const response = await axiosInstance.get(`/users/archived`);
      setArchivedUsers(response.data);
    } catch (error) {
      toast.error('Arşiv kullanıcıları yüklenirken hata oluştu');
    }
  };

  // Invite user
  const handleInviteUser = async (e) => {
    e.preventDefault();
    
    // Validate phone if provided
    if (phoneNumber && !validatePhoneNumber()) {
      return;
    }
    
    setInviting(true);
    setInviteError(''); // Clear previous error
    setInviteSuccess(''); // Clear previous success

    try {
      // Convert "none" to null for backend
      const dataToSend = {
        ...inviteData,
        manager_id: inviteData.manager_id === 'none' ? null : inviteData.manager_id,
        phone: phoneNumber ? `${phoneCountryCode}${phoneNumber}` : null
      };
      
      console.log('🚀 Sending invitation...', dataToSend);
      
      const response = await axiosInstance.post('/users/invite', dataToSend);
      
      console.log('✅ Invitation response:', response.data);
      
      // Show success message in modal
      setInviteSuccess(`Davet başarıyla gönderildi! ${inviteData.email} adresine e-posta gönderildi.`);
      
      // Clear form
      setInviteData({ email: '', role_id: '', manager_id: 'none' });
      setPhoneCountryCode(defaultCountryCode);
      setPhoneNumber('');
      setPhoneError('');
      
      // Refresh data
      fetchUsers();
      fetchCompanyStats();
      fetchPendingInvitations();
      
      // Close modal after 3 seconds
      setTimeout(() => {
        setInviteDialogOpen(false);
        setInviteSuccess('');
      }, 3000);
      
    } catch (error) {
      console.error('❌ Invitation error:', error);
      const errorMessage = error.response?.data?.detail || 'Davet gönderilirken hata oluştu';
      
      // Check if it's the cross-company invite error
      if (errorMessage.includes('başka bir hesap sahibi')) {
        setInviteError(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setInviting(false);
    }
  };

  // Start editing user
  const handleEditUser = (userItem) => {
    const user = getUserData(userItem);
    const role = getRoleData(userItem);
    setEditingUserId(user.id);
    setEditUserData({
      full_name: user.full_name,
      email: user.email,
      role_id: role?.id || user.role_id || '',
      position_id: user.position_id || user.role_id || '',
      department_id: user.department_id || 'none',
      manager_id: user.manager_id || 'none',
      is_active: user.is_active,
      employee_id: user.employee_id || generateEmployeeId(), // Otomatik oluştur
      phone: user.phone || ''
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditUserData({});
  };

  // Update user
  const handleUpdateUser = async (userId) => {
    setUpdatingUser(true);
    try {
      // Convert "none" to null for backend
      const dataToSend = {
        ...editUserData,
        manager_id: editUserData.manager_id === 'none' ? null : editUserData.manager_id
      };
      
      console.log('🔄 Updating user with data:', {
        userId,
        department: dataToSend.department,
        fullData: dataToSend
      });
      
      await axiosInstance.put(`/users/${userId}`, dataToSend);
      toast.success('Kullanıcı başarıyla güncellendi');
      setEditingUserId(null);
      setEditUserData({});
      fetchUsers(); // Refresh users list
    } catch (error) {
      console.error('❌ Update error:', error);
      toast.error(error.response?.data?.detail || 'Kullanıcı güncellenirken hata oluştu');
    } finally {
      setUpdatingUser(false);
    }
  };

  // Toggle user status
  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await axiosInstance.put(`/users/${userId}/status`, {
        is_active: !currentStatus
      });
      toast.success(`Kullanıcı durumu ${!currentStatus ? 'aktif' : 'pasif'} olarak güncellendi`);
      fetchUsers(); // Refresh users list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı durumu güncellenirken hata oluştu');
    }
  };

  // Open cancel invitation modal
  const openCancelInviteModal = (invitation) => {
    setSelectedInvitation(invitation);
    setCancelInviteDialogOpen(true);
  };

  // Cancel invitation
  const handleCancelInvitation = async () => {
    if (!selectedInvitation) return;

    try {
      await axiosInstance.delete(`/users/invitation/${selectedInvitation.id}`);
      toast.success('Davet başarıyla iptal edildi');
      fetchPendingInvitations(); // Refresh pending invitations
      fetchCompanyStats(); // Refresh company stats
      setCancelInviteDialogOpen(false);
      setSelectedInvitation(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Davet iptal edilirken hata oluştu');
    }
  };

  // Resend invitation
  const handleResendInvitation = async (invitation) => {
    try {
      const response = await axiosInstance.post(`/users/invitation/${invitation.id}/resend`);
      toast.success('Davet başarıyla yeniden gönderildi');
      fetchPendingInvitations(); // Refresh pending invitations
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Davet yeniden gönderilirken hata oluştu');
    }
  };

  // Get WhatsApp link for invitation
  const handleGetWhatsAppLink = async (invitation) => {
    try {
      const response = await axiosInstance.get(`/users/invitation/${invitation.id}/whatsapp-link`);
      
      // Copy WhatsApp link to clipboard
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(response.data.whatsapp_link);
        toast.success('WhatsApp linki panoya kopyalandı! 📋');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = response.data.whatsapp_link;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('WhatsApp linki panoya kopyalandı! 📋');
      }
      
      // Optional: Open WhatsApp directly
      window.open(response.data.whatsapp_link, '_blank');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'WhatsApp linki oluşturulurken hata oluştu');
    }
  };

  // Activate inactive user
  const handleActivateUser = async (userId, fullName) => {
    if (!window.confirm(`${fullName} kullanıcısını aktif hale getirmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await axiosInstance.put(`/users/${userId}/status`, {
        is_active: true
      });
      toast.success(`${fullName} kullanıcısı aktif hale getirildi`);
      fetchUsers(); // Refresh users list
      fetchCompanyStats(); // Refresh company stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı aktif hale getirilirken hata oluştu');
    }
  };

  // Restore archived user
  // Check deletion impact and open delete modal
  const openDeleteUserModal = async (userItem) => {
    try {
      setSelectedUserToDelete(userItem);
      setDeleteUserDialogOpen(true);
      setDeletionImpact(null);
      
      // Calculate deletion impact
      const response = await axiosInstance.get(`/users/${getUserData(userItem).id}/deletion-impact`);
      setDeletionImpact(response.data);
    } catch (error) {
      toast.error('Silme etkisi hesaplanırken hata oluştu');
      setDeleteUserDialogOpen(false);
      setSelectedUserToDelete(null);
    }
  };

  // Permanently delete user
  const handlePermanentDelete = async () => {
    if (!selectedUserToDelete) return;

    if (!window.confirm(`${selectedUserToDelete.user.full_name} kullanıcısını KALICI olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`)) {
      return;
    }

    try {
      setDeletingUser(true);
      const response = await axiosInstance.delete(`/users/${selectedUserToDelete.user.id}/permanent`);
      
      toast.success(`${selectedUserToDelete.user.full_name} kullanıcısı kalıcı olarak silindi`);
      
      if (response.data.subordinates_affected > 0) {
        toast.info(`${response.data.subordinates_affected} ast çalışan yöneticisiz kaldı`);
      }
      
      fetchUsers(); // Refresh users list
      fetchCompanyStats(); // Refresh company stats
      setDeleteUserDialogOpen(false);
      setSelectedUserToDelete(null);
      setDeletionImpact(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı silinirken hata oluştu');
    } finally {
      setDeletingUser(false);
    }
  };

  // Archive user (İşten Ayrıldı)
  const handleArchiveUser = async () => {
    if (!selectedUserToArchive) return;

    try {
      setArchivingUser(true);
      await axiosInstance.post(`/users/${selectedUserToArchive.id}/archive`);
      
      toast.success(`${selectedUserToArchive.full_name} arşiv kullanıcılar arasına taşındı`);
      
      // Refresh data
      fetchUsers();
      fetchCompanyStats();
      fetchArchivedUsers();
      
      // Close modal
      setArchiveUserDialogOpen(false);
      setSelectedUserToArchive(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı arşivlenirken hata oluştu');
    } finally {
      setArchivingUser(false);
    }
  };

  // Fetch suggested managers based on role hierarchy
  const fetchSuggestedManagers = async (roleId) => {
    try {
      const response = await axiosInstance.get('/role-hierarchy/manager-suggestions/${roleId}');
      setSuggestedManagers(response.data);
      
      // Auto-select first suggested manager if available
      if (response.data.length > 0) {
        setInviteData(prev => ({
          ...prev,
          manager_id: response.data[0].id
        }));
        toast.info(`${response.data[0].full_name} otomatik olarak yönetici önerildi`);
      } else {
        setInviteData(prev => ({ ...prev, manager_id: 'none' }));
        setSuggestedManagers([]);
      }
    } catch (error) {
      console.error('Error fetching suggested managers:', error);
      setSuggestedManagers([]);
      setInviteData(prev => ({ ...prev, manager_id: 'none' }));
    }
  };

  // Handle role selection change
  const handleRoleChange = (roleId) => {
    setInviteData(prev => ({ ...prev, role_id: roleId, manager_id: 'none' }));
    if (roleId) {
      fetchSuggestedManagers(roleId);
    } else {
      setSuggestedManagers([]);
    }
  };

  // Create user with credentials
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!createUserData.email || !createUserData.full_name || !createUserData.role_id) {
      toast.error('E-posta, ad soyad ve rol zorunludur');
      return;
    }
    
    // Validate phone if provided
    if (createPhoneNumber && !validateCreatePhoneNumber()) {
      return;
    }

    try {
      setCreatingUser(true);
      
      // Combine country code and phone number if provided
      const dataToSend = {
        ...createUserData,
        phone: createPhoneNumber ? `${createPhoneCountryCode}${createPhoneNumber}` : ''
      };
      
      const response = await axiosInstance.post('/users/create-with-credentials', dataToSend);
      
      setCreatedCredentials(response.data);
      setShowCredentialsModal(true);
      setCreateUserDialogOpen(false);
      
      // Reset form
      setCreateUserData({
        email: '',
        full_name: '',
        role_id: '',
        phone: '',
        position_id: null,
        department_id: null,
        manager_id: null,
        send_method: 'email'
      });
      setCreatePhoneCountryCode(defaultCountryCode);
      setCreatePhoneNumber('');
      setCreatePhoneError('');
      
      // Refresh users list
      fetchUsers();
      fetchCompanyStats();
      
      toast.success('Kullanıcı başarıyla oluşturuldu!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı oluşturulurken hata oluştu');
    } finally {
      setCreatingUser(false);
    }
  };

  // Approve change request
  const handleApproveChangeRequest = async (requestId) => {
    try {
      await axiosInstance.put(`/user-change-requests/${requestId}/approve`);
      toast.success('Değişiklik talebi onaylandı');
      fetchChangeRequests();
      fetchUsers(); // Refresh user list to show updated data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onaylama sırasında hata oluştu');
    }
  };

  // Reject change request
  const handleRejectChangeRequest = async (requestId) => {
    try {
      await axiosInstance.put(`/user-change-requests/${requestId}/reject`);
      toast.success('Değişiklik talebi reddedildi');
      fetchChangeRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reddetme sırasında hata oluştu');
    }
  };

  // Check deletion impact
  const handleCheckDeletionImpact = async (userId, userName) => {
    try {
      setDeletingUser(true);
      console.log('Checking deletion impact for user:', userId);
      const response = await axiosInstance.get(`/users/${userId}/deletion-impact`);
      console.log('Deletion impact response:', response.data);
      setDeletionImpact(response.data);
      setSelectedUserToDelete({ id: userId, name: userName });
      
      // Check if user can be deleted (no critical dependencies)
      if (response.data.critical_dependencies && response.data.critical_dependencies.length > 0) {
        console.log('Critical dependencies found:', response.data.critical_dependencies);
        toast.error(`Bu kullanıcı silinemez: ${response.data.critical_dependencies.join(', ')}`);
        setDeletingUser(false);
        return;
      }
      
      console.log('User can be deleted, showing confirmation modal');
      // Show confirmation modal
      setShowDeleteConfirmation(true);
      setDeletingUser(false);
    } catch (error) {
      console.error('Deletion impact check error:', error);
      toast.error(error.response?.data?.detail || 'Silme kontrolü başarısız');
      setDeletingUser(false);
    }
  };

  // Permanently delete user
  const handlePermanentDeleteUser = async () => {
    if (!selectedUserToDelete) return;
    
    try {
      setDeletingUser(true);
      await axiosInstance.delete(`/users/${selectedUserToDelete.id}/permanent`);
      toast.success('Kullanıcı kalıcı olarak silindi');
      setShowDeleteConfirmation(false);
      setSelectedUserToDelete(null);
      setDeletionImpact(null);
      fetchUsers();
      fetchCompanyStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı silinirken hata oluştu');
    } finally {
      setDeletingUser(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Panoya kopyalandı');
  };

  useEffect(() => {
    fetchCountryCodes();
    fetchUsers();
    fetchRoles();
    fetchManagers();
    fetchDepartments();
    fetchCompanyStats();
    fetchPendingInvitations();
    fetchArchivedUsers();
    fetchChangeRequests();
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner mr-3"></div>
          <span>Kullanıcılar yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl">
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Kullanıcı Yönetimi</h3>
              <p className="text-gray-600 mt-2">Takım üyelerini davet edin ve kullanıcı yetkilerini yönetin</p>
            </div>
            {companyStats && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 min-w-[200px]">
                <div className="text-sm font-medium text-teal-800 mb-1">{companyStats.company_name}</div>
                <div className="text-2xl font-bold text-teal-900">
                  {companyStats.current_user_count} / {companyStats.user_limit}
                </div>
                <div className="text-xs text-teal-700 mt-1">
                  {companyStats.remaining_slots > 0 
                    ? `${companyStats.remaining_slots} slot kaldı` 
                    : 'Kullanıcı limiti dolu'
                  }
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Invite New User */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h4 className="font-semibold text-gray-900">Kullanıcı Yönetimi</h4>
                <p className="text-sm text-gray-600">Takımınıza yeni üye ekleyin veya davet gönderin</p>
              </div>
              <div className="flex space-x-3">
                {/* Create User Button */}
                <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={companyStats && companyStats.remaining_slots <= 0}
                      title={companyStats && companyStats.remaining_slots <= 0 
                        ? `Kullanıcı limiti dolu (${companyStats.user_limit} / ${companyStats.user_limit})` 
                        : 'Doğrudan kullanıcı ekle ve bilgileri paylaş'
                      }
                    >
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Kullanıcı Ekle
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Yeni Kullanıcı Oluştur</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                        <p className="font-medium mb-1">ℹ️ Bilgilendirme</p>
                        <p>Sistem otomatik güvenli şifre oluşturacak ve seçtiğiniz yöntemle kullanıcıya gönderecektir.</p>
                      </div>

                      <div>
                        <Label htmlFor="create-full-name" className="form-label">Ad Soyad *</Label>
                        <Input
                          id="create-full-name"
                          type="text"
                          value={createUserData.full_name}
                          onChange={(e) => setCreateUserData({...createUserData, full_name: e.target.value})}
                          className="form-input"
                          placeholder="Ahmet Yılmaz"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="create-email" className="form-label">E-posta Adresi *</Label>
                        <Input
                          id="create-email"
                          type="email"
                          value={createUserData.email}
                          onChange={(e) => setCreateUserData({...createUserData, email: e.target.value})}
                          className="form-input"
                          placeholder="ahmet@sirket.com"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="create-phone" className="form-label">
                          Telefon (WhatsApp için - Opsiyonel)
                        </Label>
                        <div className="flex gap-2">
                          {/* Country Code Dropdown */}
                          <Select value={createPhoneCountryCode} onValueChange={setCreatePhoneCountryCode}>
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {countryCodes.map((country) => (
                                <SelectItem key={country.code} value={country.code}>
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{country.flag}</span>
                                    <span className="font-mono text-sm">{country.code}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Phone Number Input */}
                          <div className="flex-1">
                            <Input
                              id="create-phone"
                              type="text"
                              inputMode="numeric"
                              value={createPhoneNumber}
                              onChange={handleCreatePhoneNumberChange}
                              onBlur={validateCreatePhoneNumber}
                              className={`form-input ${createPhoneError ? 'border-red-500' : ''}`}
                              placeholder="5551234567"
                              maxLength={10}
                            />
                            {createPhoneError && (
                              <p className="text-xs text-red-600 mt-1">
                                {createPhoneError}
                              </p>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Sadece rakam girin. Örnek: {createPhoneCountryCode} 5551234567
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="create-position" className="form-label">Kullanıcı Pozisyonu *</Label>
                        <Select 
                          value={createUserData.position_id || createUserData.role_id} 
                          onValueChange={(value) => setCreateUserData({...createUserData, role_id: value, position_id: value})}
                          required
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Pozisyon seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map((role) => (
                              <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="create-department" className="form-label">Departman</Label>
                        <Select 
                          value={createUserData.department_id || 'none'} 
                          onValueChange={(value) => setCreateUserData({...createUserData, department_id: value === 'none' ? null : value})}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Departman seçin (opsiyonel)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Departman Yok</SelectItem>
                            {departments.map((dept) => (
                              <SelectItem key={dept.id} value={dept.id}>
                                <span className="inline-flex items-center">
                                  {dept.name}
                                  {dept.is_default && (
                                    <Badge variant="outline" className="ml-2 text-xs">Varsayılan</Badge>
                                  )}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="create-manager" className="form-label">Yönetici</Label>
                        <Select 
                          value={createUserData.manager_id || 'none'} 
                          onValueChange={(value) => setCreateUserData({...createUserData, manager_id: value === 'none' ? null : value})}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Yönetici seçin (opsiyonel)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Yönetici Yok</SelectItem>
                            {managers.map((manager) => (
                              <SelectItem key={manager.id} value={manager.id}>
                                {manager.full_name} - {manager.role_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="form-label">Bilgileri Nasıl Gönderilsin? *</Label>
                        <div className="space-y-2 mt-2">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="send_method"
                              value="email"
                              checked={createUserData.send_method === 'email'}
                              onChange={(e) => setCreateUserData({...createUserData, send_method: e.target.value})}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-sm">📧 Email ile gönder</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="send_method"
                              value="whatsapp"
                              checked={createUserData.send_method === 'whatsapp'}
                              onChange={(e) => setCreateUserData({...createUserData, send_method: e.target.value})}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-sm">💬 WhatsApp ile paylaş</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="send_method"
                              value="both"
                              checked={createUserData.send_method === 'both'}
                              onChange={(e) => setCreateUserData({...createUserData, send_method: e.target.value})}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-sm">📧 💬 Her ikisi ile</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex justify-end space-x-3 pt-4">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setCreateUserDialogOpen(false)}
                          disabled={creatingUser}
                        >
                          İptal
                        </Button>
                        <Button 
                          type="submit" 
                          className="btn-primary"
                          disabled={creatingUser}
                        >
                          {creatingUser ? (
                            <>
                              <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                              Oluşturuluyor...
                            </>
                          ) : (
                            <>
                              <CheckIcon className="w-4 h-4 mr-2" />
                              Oluştur ve Gönder
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Invite User Button */}
                <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-primary" 
                      data-testid="invite-user-btn"
                      disabled={companyStats && companyStats.remaining_slots <= 0}
                      title={companyStats && companyStats.remaining_slots <= 0 
                        ? `Kullanıcı limiti dolu (${companyStats.user_limit} / ${companyStats.user_limit})` 
                        : 'Yeni kullanıcı davet et'
                      }
                    >
                      <PlusIcon className="w-4 h-4 mr-2" />
                      {companyStats && companyStats.remaining_slots <= 0 
                        ? 'Limit Dolu' 
                        : 'Davet Gönder'
                      }
                    </Button>
                  </DialogTrigger>
                <DialogContent data-testid="invite-dialog">
                  <DialogHeader>
                    <DialogTitle>Yeni Kullanıcı Davet Et</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleInviteUser} className="space-y-4">
                    <div>
                      <Label htmlFor="invite-email" className="form-label">E-posta Adresi</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={inviteData.email}
                        onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                        className="form-input"
                        placeholder="kullanici@sirket.com"
                        required
                        data-testid="invite-email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="invite-role" className="form-label">Rol</Label>
                      <Select value={inviteData.role_id} onValueChange={handleRoleChange}>
                        <SelectTrigger data-testid="invite-role-select">
                          <SelectValue placeholder="Rol seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.id} value={role.id}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="invite-manager" className="form-label">
                        Hiyerarşi (Yönetici) 
                        {suggestedManagers.length > 0 && (
                          <Badge className="ml-2 bg-green-100 text-green-800">
                            {suggestedManagers.length} önerilen
                          </Badge>
                        )}
                      </Label>
                      <Select value={inviteData.manager_id} onValueChange={(value) => setInviteData({...inviteData, manager_id: value})}>
                        <SelectTrigger data-testid="invite-manager-select">
                          <SelectValue placeholder="Yönetici seçin (opsiyonel)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Yönetici Yok</SelectItem>
                          
                          {/* Suggested Managers First */}
                          {suggestedManagers.length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-50">
                                🎯 Hiyerarşiye Göre Önerilen
                              </div>
                              {suggestedManagers.map((manager) => (
                                <SelectItem key={`suggested-${manager.id}`} value={manager.id}>
                                  <div className="flex items-center justify-between w-full">
                                    <span className="font-medium">✨ {manager.full_name}</span>
                                    <Badge variant="default" className="ml-2 text-xs bg-green-600">
                                      {manager.role_name}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                              <div className="border-t border-gray-200 my-1"></div>
                            </>
                          )}
                          
                          {/* All Other Managers */}
                          {managers.filter(manager => !suggestedManagers.find(s => s.id === manager.id)).map((manager) => (
                            <SelectItem key={manager.id} value={manager.id}>
                              <div className="flex items-center justify-between w-full">
                                <span>{manager.full_name}</span>
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {manager.role_name}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        {suggestedManagers.length > 0 ? 
                          '✨ Rol hiyerarşisine göre otomatik öneriler yapılmaktadır' :
                          'Bu kullanıcının avans talepleri seçilen yöneticiye gönderilecek'
                        }
                      </p>
                    </div>

                    {/* Phone Number */}
                    <div>
                      <Label htmlFor="invite-phone" className="form-label">
                        Telefon (WhatsApp için - Opsiyonel)
                      </Label>
                      <div className="flex gap-2">
                        {/* Country Code Dropdown */}
                        <Select value={phoneCountryCode} onValueChange={setPhoneCountryCode}>
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {countryCodes.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{country.flag}</span>
                                  <span className="font-mono text-sm">{country.code}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Phone Number Input */}
                        <div className="flex-1">
                          <Input
                            id="invite-phone"
                            type="text"
                            inputMode="numeric"
                            value={phoneNumber}
                            onChange={handlePhoneNumberChange}
                            onBlur={validatePhoneNumber}
                            className={`form-input ${phoneError ? 'border-red-500' : ''}`}
                            placeholder="5551234567"
                            maxLength={10}
                          />
                          {phoneError && (
                            <p className="text-xs text-red-600 mt-1">
                              {phoneError}
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Sadece rakam girin. Örnek: {phoneCountryCode} 5551234567
                      </p>
                    </div>

                    {/* Error Message */}
                    {inviteError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-red-800 font-medium">
                            {inviteError}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Success Message */}
                    {inviteSuccess && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                        <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-green-800 font-medium">
                            {inviteSuccess}
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            Bu pencere 3 saniye içinde otomatik kapanacak...
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setInviteDialogOpen(false);
                          setInviteData({ email: '', role_id: '', manager_id: 'none' });
                          setPhoneCountryCode(defaultCountryCode);
                          setPhoneNumber('');
                          setPhoneError('');
                          setInviteError('');
                          setInviteSuccess('');
                        }}
                        disabled={inviting}
                        className="flex-1"
                      >
                        İptal
                      </Button>
                      <Button
                        type="submit"
                        disabled={inviting || !inviteData.email || !inviteData.role_id}
                        className="btn-primary flex-1"
                        data-testid="send-invite-btn"
                      >
                        {inviting ? (
                          <div className="loading-spinner mr-2"></div>
                        ) : (
                          <PlusIcon className="w-4 h-4 mr-2" />
                        )}
                        <span>{inviting ? 'Gönderiliyor...' : 'Davet Gönder'}</span>
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>
          </Card>

          {/* User Change Requests Section */}
          {changeRequests.length > 0 && (
            <Card className="p-6 border-orange-200 bg-orange-50">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <AlertTriangleIcon className="w-5 h-5 mr-2 text-orange-600" />
                    Bekleyen Değişiklik Talepleri
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Kullanıcıların profil değişiklik taleplerini onaylayın veya reddedin
                  </p>
                </div>
                <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                  {changeRequests.length} Talep
                </Badge>
              </div>

              <div className="space-y-3">
                {loadingChangeRequests ? (
                  <div className="text-center py-4 text-gray-600">
                    <RefreshCwIcon className="w-5 h-5 animate-spin inline mr-2" />
                    Yükleniyor...
                  </div>
                ) : (
                  changeRequests.map((request, index) => {
                    const role = roles.find(r => r.id === request.requested_changes?.role_id);
                    const currentRole = roles.find(r => r.id === request.current_values?.role_id);
                    
                    return (
                      <div key={index} className="bg-white p-4 rounded-lg border border-orange-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <UserIcon className="w-4 h-4 text-gray-600" />
                              <span className="font-semibold text-gray-900">
                                {request.requester_name}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {request.requester_email}
                              </Badge>
                            </div>

                            <div className="space-y-2 text-sm">
                              {/* Name Change */}
                              {request.requested_changes?.full_name && 
                               request.requested_changes.full_name !== request.current_values?.full_name && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-600 min-w-[80px]">Ad Soyad:</span>
                                  <span className="text-red-600 line-through">
                                    {request.current_values?.full_name || '-'}
                                  </span>
                                  <span className="text-gray-400">→</span>
                                  <span className="text-green-600 font-medium">
                                    {request.requested_changes.full_name}
                                  </span>
                                </div>
                              )}

                              {/* Email Change */}
                              {request.requested_changes?.email && 
                               request.requested_changes.email !== request.current_values?.email && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-600 min-w-[80px]">E-posta:</span>
                                  <span className="text-red-600 line-through">
                                    {request.current_values?.email || '-'}
                                  </span>
                                  <span className="text-gray-400">→</span>
                                  <span className="text-green-600 font-medium">
                                    {request.requested_changes.email}
                                  </span>
                                </div>
                              )}

                              {/* Role Change */}
                              {request.requested_changes?.role_id && 
                               request.requested_changes.role_id !== request.current_values?.role_id && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-600 min-w-[80px]">Pozisyon:</span>
                                  <span className="text-red-600 line-through">
                                    {currentRole?.name || '-'}
                                  </span>
                                  <span className="text-gray-400">→</span>
                                  <span className="text-green-600 font-medium">
                                    {role?.name || 'Bilinmiyor'}
                                  </span>
                                </div>
                              )}

                              <div className="text-xs text-gray-500 mt-2">
                                Talep Tarihi: {new Date(request.requested_at).toLocaleDateString('tr-TR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                          </div>

                          <div className="flex space-x-2 ml-4">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleApproveChangeRequest(request.id)}
                            >
                              <CheckIcon className="w-4 h-4 mr-1" />
                              Onayla
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-600 hover:bg-red-50"
                              onClick={() => handleRejectChangeRequest(request.id)}
                            >
                              <XIcon className="w-4 h-4 mr-1" />
                              Reddet
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          )}

          {/* Users List */}
          <Card className="p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Takım Üyeleri</h4>
            <div className="space-y-4">{users.map((userItem, index) => (
                <div key={index} className={`relative p-4 pt-6 rounded-lg transition-colors ${editingUserId === getUserData(userItem).id ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50 hover:bg-gray-100'}`} data-testid={`user-row-${index}`}>
                  {editingUserId === getUserData(userItem).id ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-800 mb-2">Kullanıcı Düzenleniyor</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="form-label text-sm">Ad Soyad</Label>
                          <Input
                            value={editUserData.full_name || ''}
                            onChange={(e) => setEditUserData(prev => ({...prev, full_name: e.target.value}))}
                            className="form-input"
                            placeholder="Kullanıcı adı"
                          />
                        </div>
                        <div>
                          <Label className="form-label text-sm">E-posta</Label>
                          <Input
                            value={editUserData.email || ''}
                            onChange={(e) => setEditUserData(prev => ({...prev, email: e.target.value}))}
                            className="form-input"
                            placeholder="E-posta adresi"
                            type="email"
                          />
                        </div>
                        <div>
                          <Label className="form-label text-sm">Pozisyon</Label>
                          <Select 
                            value={editUserData.position_id || editUserData.role_id} 
                            onValueChange={(value) => setEditUserData(prev => ({...prev, role_id: value, position_id: value}))}
                          >
                            <SelectTrigger className="form-input">
                              <SelectValue placeholder="Pozisyon seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {roles.map((role) => (
                                <SelectItem key={role.id} value={role.id}>
                                  {role.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="form-label text-sm">Yönetici</Label>
                          <Select value={editUserData.manager_id || 'none'} onValueChange={(value) => setEditUserData(prev => ({...prev, manager_id: value}))}>
                            <SelectTrigger className="form-input">
                              <SelectValue placeholder="Yönetici seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Yönetici Yok</SelectItem>
                              {managers.map((manager) => (
                                <SelectItem key={manager.id} value={manager.id}>
                                  <div className="flex items-center justify-between w-full">
                                    <span>{manager.full_name}</span>
                                    <Badge variant="outline" className="ml-2 text-xs">
                                      {manager.role_name}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="form-label text-sm">Departman</Label>
                          <Select
                            value={editUserData.department_id || 'none'}
                            onValueChange={(value) => setEditUserData(prev => ({
                              ...prev, 
                              department_id: value === 'none' ? null : value
                            }))}
                          >
                            <SelectTrigger className="form-input">
                              <SelectValue placeholder="Departman seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Departman Seçilmedi</SelectItem>
                              {departments.map((dept) => (
                                <SelectItem key={dept.id} value={dept.id}>
                                  {dept.name}
                                  {dept.is_default && (
                                    <Badge variant="outline" className="ml-2 text-xs">Varsayılan</Badge>
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="form-label text-sm">Çalışan ID</Label>
                          <Input
                            value={editUserData.employee_id || ''}
                            onChange={(e) => setEditUserData(prev => ({...prev, employee_id: e.target.value}))}
                            className="form-input"
                            placeholder="Örn: EMP-001"
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-blue-200">
                        {/* Status Toggle - Only for non-Super Admin users - Check carefully */}
                        {(() => {
                          // Multiple layers of protection for Super Admin detection
                          const currentRole = roles.find(role => role.id === editUserData.role_id);
                          const currentRoleName = currentRole?.name || '';
                          const originalRoleName = getRoleData(userItem)?.name || '';
                          
                          // Check all possible Super Admin variations
                          const superAdminChecks = [
                            currentRoleName === 'Super Admin',
                            currentRoleName === 'Süper Admin', 
                            currentRoleName.toLowerCase().includes('super'),
                            originalRoleName === 'Super Admin',
                            originalRoleName === 'Süper Admin',
                            originalRoleName.toLowerCase().includes('super')
                          ];
                          
                          const isSuperAdmin = superAdminChecks.some(check => check);
                          return !isSuperAdmin;
                        })() && (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => handleToggleUserStatus(getUserData(userItem).id, getUserData(userItem).is_active)}
                              disabled={updatingUser}
                              className={getUserData(userItem).is_active ? "text-red-600 hover:text-red-700 border-red-300 hover:border-red-400" : "text-green-600 hover:text-green-700 border-green-300 hover:border-green-400"}
                            >
                              {getUserData(userItem).is_active ? (
                                <>
                                  <XIcon className="w-4 h-4 mr-2" />
                                  Pasif Yap
                                </>
                              ) : (
                                <>
                                  <CheckIcon className="w-4 h-4 mr-2" />
                                  Aktif Et
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setSelectedUserToArchive(getUserData(userItem));
                                setArchiveUserDialogOpen(true);
                              }}
                              disabled={archivingUser}
                              className="text-orange-600 hover:text-orange-700 border-orange-300 hover:border-orange-400"
                              title="Kullanıcı işten ayrıldı - Arşive taşı"
                            >
                              <AlertTriangleIcon className="w-4 h-4 mr-2" />
                              İşten Ayrıldı
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleCheckDeletionImpact(getUserData(userItem).id, getUserData(userItem).full_name)}
                              disabled={deletingUser}
                              className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                              title="Kullanıcıyı kalıcı olarak sil (sadece işlemi olmayan kullanıcılar)"
                            >
                              <TrashIcon className="w-4 h-4 mr-2" />
                              Sil
                            </Button>
                          </>
                        )}

                        <div className="flex items-center space-x-3">
                          <Button
                            variant="outline"
                            onClick={handleCancelEdit}
                            disabled={updatingUser}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <XIcon className="w-4 h-4 mr-2" />
                            İptal
                          </Button>
                          <Button
                            onClick={() => handleUpdateUser(getUserData(userItem).id)}
                            disabled={updatingUser || !editUserData.full_name || !editUserData.email || !editUserData.role_id}
                            className="btn-primary"
                          >
                            {updatingUser ? (
                              <div className="loading-spinner mr-2"></div>
                            ) : (
                              <CheckIcon className="w-4 h-4 mr-2" />
                            )}
                            <span>{updatingUser ? 'Güncelleniyor...' : 'Kaydet'}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <>
                      {/* Super Admin Crown Badge - Top Left (Outside of flex container) */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('👑 Crown button clicked!', getUserData(userItem));
                          toggleSuperAdmin(userItem);
                        }}
                        disabled={updatingUser}
                        className={`absolute top-1 left-1 p-2 rounded-full transition-all transform hover:scale-110 z-20 cursor-pointer ${
                          getUserData(userItem).is_super_admin
                            ? 'bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        title={getUserData(userItem).is_super_admin ? 'Süper Admin (Tıkla: Normal Yap)' : 'Normal Kullanıcı (Tıkla: Süper Admin Yap)'}
                      >
                        <CrownIcon 
                          className={`w-4 h-4 ${
                            getUserData(userItem).is_super_admin ? 'text-white' : 'text-gray-600'
                          }`}
                          fill={getUserData(userItem).is_super_admin ? 'currentColor' : 'none'}
                        />
                      </button>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-teal-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900" data-testid={`user-name-${index}`}>
                                {getUserData(userItem).full_name}
                              </p>
                              {getUserData(userItem).is_super_admin && (
                                <Badge className="bg-yellow-500 text-white text-xs">
                                  👑 Tam Yetki
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500" data-testid={`user-email-${index}`}>
                              {getUserData(userItem).email}
                            </p>
                            {userItem.manager && (
                              <p className="text-xs text-blue-600" data-testid={`user-manager-${index}`}>
                                👤 Yönetici: {userItem.manager.full_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                        {/* Pozisyon Sütunu */}
                        <div className="text-center min-w-[120px]">
                          <p className="text-xs text-gray-500 mb-1">Pozisyon</p>
                          <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700">
                            {(() => {
                              const user = getUserData(userItem);
                              console.log(`👤 User ${user.full_name}:`, {
                                position_id: user.position_id,
                                role_id: user.role_id,
                                roles_count: roles.length,
                                first_role: roles[0]
                              });
                              
                              // First try position_id
                              if (user.position_id) {
                                const position = roles.find(r => r.id === user.position_id);
                                if (position) return position.name;
                              }
                              
                              // Fallback to role
                              const roleData = getRoleData(userItem);
                              if (roleData) return roleData.name;
                              
                              return 'Belirsiz';
                            })()}
                          </Badge>
                        </div>
                        
                        {/* Departman Sütunu */}
                        <div className="text-center min-w-[120px]">
                          <p className="text-xs text-gray-500 mb-1">Departman</p>
                          <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700">
                            {(() => {
                              const user = getUserData(userItem);
                              console.log(`🏢 User ${user.full_name} department:`, {
                                department_id: user.department_id,
                                departments_count: departments.length,
                                first_dept: departments[0]
                              });
                              
                              if (user.department_id) {
                                const dept = departments.find(d => d.id === user.department_id);
                                if (dept) return dept.name;
                                return 'Belirsiz';
                              }
                              return 'Atanmadı';
                            })()}
                          </Badge>
                        </div>
                        
                        <Badge 
                          variant={getUserData(userItem).is_active ? "default" : "secondary"}
                          data-testid={`user-status-${index}`}
                        >
                          {getUserData(userItem).is_active ? 'Aktif' : 'Pasif'}
                        </Badge>
                        <div className="flex items-center space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-gray-500 hover:text-gray-700"
                            onClick={() => handleEditUser(userItem)}
                            data-testid={`edit-user-${index}`}
                            title="Kullanıcıyı düzenle"
                          >
                            <EditIcon className="w-4 h-4" />
                          </Button>
                        </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
              
              {users.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p>Henüz kullanıcı bulunmuyor</p>
                  <p className="text-sm">İlk kullanıcıyı davet edin!</p>
                </div>
              )}
            </div>
          </Card>

          {/* Pending Invitations */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h4 className="font-semibold text-gray-900">Davet Bekleyen Kullanıcılar</h4>
                <p className="text-sm text-gray-600">Henüz kaydını tamamlamamış kullanıcılar</p>
              </div>
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                {pendingInvitations.length} bekleyen
              </Badge>
            </div>
            
            <div className="space-y-3">
              {pendingInvitations.map((invitation, index) => (
                <div 
                  key={invitation.id} 
                  className="p-4 rounded-lg bg-orange-50 border border-orange-200 hover:bg-orange-100 transition-colors"
                  data-testid={`pending-invitation-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-orange-700" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900" data-testid={`pending-email-${index}`}>
                          {invitation.email}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>👤 {invitation.role_name}</span>
                          {invitation.manager_name && (
                            <span>🧑‍💼 Yönetici: {invitation.manager_name}</span>
                          )}
                          <span>📧 Davet eden: {invitation.invited_by}</span>
                        </div>
                        <p className="text-xs text-orange-700 mt-1">
                          Son tarih: {new Date(invitation.expires_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-300">
                        Davet Bekliyor
                      </Badge>
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleResendInvitation(invitation)}
                          data-testid={`resend-invitation-${index}`}
                          title="Yeniden Davet Et"
                        >
                          <RefreshCwIcon className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-green-500 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleGetWhatsAppLink(invitation)}
                          data-testid={`whatsapp-invitation-${index}`}
                          title="WhatsApp Linki"
                        >
                          <MessageCircleIcon className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openCancelInviteModal(invitation)}
                          data-testid={`cancel-invitation-${index}`}
                          title="Daveti İptal Et"
                        >
                          <XIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {pendingInvitations.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <p>Bekleyen davet bulunmuyor</p>
                  <p className="text-sm">Yeni kullanıcılar davet ettiğinizde burada görünecek</p>
                </div>
              )}
            </div>
          </Card>

          {/* Inactive Users */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h4 className="font-semibold text-gray-900">Pasif Kullanıcılar</h4>
                <p className="text-sm text-gray-600">Hesabı pasif hale getirilmiş kullanıcılar</p>
              </div>
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                {inactiveUsers.length} pasif
              </Badge>
            </div>
            
            <div className="space-y-3">
              {inactiveUsers
                .filter(userItem => {
                  const user = getUserData(userItem);
                  return user && user.id;
                })
                .map((userItem, index) => {
                  // Safely access user data
                  const user = getUserData(userItem);
                  const role = getRoleData(userItem) || {};
                  const manager = userItem.manager || null;
                  
                  return (
                    <div 
                      key={user.id || index} 
                      className="p-4 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 transition-colors"
                      data-testid={`inactive-user-${index}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-red-700" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900" data-testid={`inactive-name-${index}`}>
                              {user.full_name || 'Bilinmeyen Kullanıcı'}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>📧 {user.email || 'N/A'}</span>
                              <span>👤 {role.name || 'N/A'}</span>
                              {manager && (
                                <span>🧑‍💼 Yönetici: {manager.full_name}</span>
                              )}
                            </div>
                        <p className="text-xs text-red-700 mt-1">
                          ⚠️ Bu kullanıcı sisteme giriş yapamaz
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge variant="secondary" className="bg-red-100 text-red-800 border-red-300">
                        Pasif
                      </Badge>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-green-600 hover:text-green-700 border-green-300 hover:border-green-400"
                        onClick={() => user.id && handleActivateUser(user.id, user.full_name)}
                        data-testid={`activate-user-${index}`}
                        title="Kullanıcıyı aktif hale getir"
                        disabled={!user.id}
                      >
                        <CheckIcon className="w-4 h-4 mr-1" />
                        Aktif Et
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
              
              {inactiveUsers.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <p>Pasif kullanıcı bulunmuyor</p>
                  <p className="text-sm">Tüm kullanıcılar aktif durumda</p>
                </div>
              )}
            </div>
          </Card>

          {/* Archived Users */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h4 className="font-semibold text-gray-900">Arşiv Kullanıcılar</h4>
                <p className="text-sm text-gray-600">İşten ayrılmış kullanıcılar (sadece kayıt amaçlı)</p>
              </div>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                {archivedUsers.length} arşivde
              </Badge>
            </div>
            
            <div className="space-y-3">
              {archivedUsers.map((archivedUser, index) => (
                <div 
                  key={archivedUser.id} 
                  className="p-4 rounded-lg bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors"
                  data-testid={`archived-user-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-purple-700" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900" data-testid={`archived-name-${index}`}>
                          {archivedUser.full_name}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>📧 {archivedUser.email}</span>
                          <span>👤 {archivedUser.role_name}</span>
                          {archivedUser.manager_name && (
                            <span>🧑‍💼 Yönetici: {archivedUser.manager_name}</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-purple-700 mt-1">
                          <span>📦 Arşivlendi: {new Date(archivedUser.archived_at).toLocaleDateString('tr-TR')}</span>
                          <span>👤 Arşivleyen: {archivedUser.archived_by}</span>
                          <span>🔖 {archivedUser.archive_reason === 'manual' ? 'Manuel' : '6 Ay Otomatik'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge variant="secondary" className="bg-purple-100 text-purple-800 border-purple-300">
                        Arşivde
                      </Badge>
                      <p className="text-xs text-gray-500 italic">
                        Aynı e-posta ile yeni hesap oluşturulabilir
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {archivedUsers.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  <p>Arşivlenmiş kullanıcı bulunmuyor</p>
                  <p className="text-sm">Pasif kullanıcıları arşivleyerek burada görebilirsiniz</p>
                </div>
              )}
            </div>
          </Card>

          {/* Coming Soon Features */}
          <Card className="p-6 bg-blue-50 border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">Yakında Geliyor</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Toplu kullanıcı işlemleri</li>
              <li>• Kullanıcı detay görüntüleme</li>
              <li>• Kullanıcı aktivite logları</li>
            </ul>
          </Card>
        </div>
      </div>

      {/* Cancel Invitation Modal */}
      <Dialog open={cancelInviteDialogOpen} onOpenChange={setCancelInviteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-red-600">
              Daveti İptal Et
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvitation && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 text-sm mb-2">İptal edilecek davet:</h4>
                <div className="space-y-1 text-sm text-red-700">
                  <p><strong>E-posta:</strong> {selectedInvitation.email}</p>
                  <p><strong>Rol:</strong> {selectedInvitation.role_name}</p>
                  {selectedInvitation.manager_name && (
                    <p><strong>Yönetici:</strong> {selectedInvitation.manager_name}</p>
                  )}
                  <p><strong>Davet eden:</strong> {selectedInvitation.invited_by}</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <h4 className="font-semibold text-yellow-800 text-sm mb-1">Uyarı:</h4>
                <p className="text-sm text-yellow-700">
                  Bu işlem geri alınamaz. Davet iptal edildikten sonra kullanıcı kayıt olamayacak.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setCancelInviteDialogOpen(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Vazgeç
                </Button>
                <Button 
                  onClick={handleCancelInvitation}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <XIcon className="w-4 h-4 mr-2" />
                  Daveti İptal Et
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Modal */}
      <Dialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-red-600 flex items-center">
              <AlertTriangleIcon className="w-5 h-5 mr-2" />
              Kullanıcıyı Kalıcı Sil
            </DialogTitle>
          </DialogHeader>
          
          {selectedUserToDelete && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-gray-800 mb-1">{selectedUserToDelete.user.full_name}</h4>
                <p className="text-sm text-gray-600">{selectedUserToDelete.user.email}</p>
              </div>

              {/* Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <h4 className="font-semibold text-red-800 text-sm mb-2 flex items-center">
                  <AlertTriangleIcon className="w-4 h-4 mr-1" />
                  ⚠️ DİKKAT: Bu işlem geri alınamaz!
                </h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Kullanıcı verileri kalıcı olarak silinecek</li>
                  <li>• Arşivden geri getirilemeyecek</li>
                  <li>• İlişkili veriler güncellenerek korunacak</li>
                </ul>
              </div>

              {/* Deletion Impact */}
              {deletionImpact && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-800">Silme Etkisi:</h4>
                  
                  {/* Critical Dependencies */}
                  {deletionImpact.critical_dependencies?.length > 0 && (
                    <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                      <h5 className="font-medium text-red-800 mb-1">❌ Kritik Bağımlılıklar (Silme engellendi):</h5>
                      <ul className="text-sm text-red-700">
                        {deletionImpact.critical_dependencies.map((dep, index) => (
                          <li key={index}>• {dep}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Warnings */}
                  {deletionImpact.warnings?.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                      <h5 className="font-medium text-yellow-800 mb-1">⚠️ Uyarılar:</h5>
                      <ul className="text-sm text-yellow-700">
                        {deletionImpact.warnings.map((warning, index) => (
                          <li key={index}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Affected Entities */}
                  {deletionImpact.affected_entities && Object.keys(deletionImpact.affected_entities).length > 0 && (
                    <div className="bg-blue-50 border border-blue-300 rounded-lg p-3">
                      <h5 className="font-medium text-blue-800 mb-1">📊 Etkilenecek Veriler:</h5>
                      <ul className="text-sm text-blue-700">
                        {Object.entries(deletionImpact.affected_entities).map(([key, count]) => (
                          <li key={key}>• {key}: {count} adet</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Confirmation Input */}
              {deletionImpact && !deletionImpact.critical_dependencies?.length && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700 mb-2">
                    Devam etmek için <strong>"{selectedUserToDelete.user.full_name}"</strong> kullanıcı adını yazın:
                  </p>
                  <Input
                    type="text"
                    placeholder="Kullanıcı adını yazın"
                    className="text-sm"
                    id="delete-confirmation"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDeleteUserDialogOpen(false);
                    setSelectedUserToDelete(null);
                    setDeletionImpact(null);
                  }}
                  disabled={deletingUser}
                  className="text-gray-600 hover:text-gray-800"
                >
                  İptal
                </Button>
                
                {deletionImpact && !deletionImpact.critical_dependencies?.length && (
                  <Button 
                    onClick={handlePermanentDelete}
                    disabled={deletingUser}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deletingUser ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Siliniyor...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="w-4 h-4 mr-1" />
                        Kalıcı Sil
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Credentials Display Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>✅ Kullanıcı Başarıyla Oluşturuldu</DialogTitle>
          </DialogHeader>
          
          {createdCredentials && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium mb-2">
                  {createdCredentials.full_name} başarıyla oluşturuldu!
                </p>
                <p className="text-xs text-green-700">
                  {createdCredentials.credentials_sent 
                    ? 'Giriş bilgileri kullanıcıya gönderildi.' 
                    : 'Lütfen aşağıdaki bilgileri manuel olarak kullanıcıya iletin.'}
                </p>
              </div>

              {createdCredentials.send_errors && createdCredentials.send_errors.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 font-medium mb-2">⚠️ Gönderim Uyarıları:</p>
                  <ul className="text-xs text-yellow-700 list-disc list-inside">
                    {createdCredentials.send_errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-gray-900">Giriş Bilgileri:</h4>
                
                <div>
                  <Label className="text-xs text-gray-600">Kullanıcı Adı (E-posta)</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Input
                      value={createdCredentials.email}
                      readOnly
                      className="flex-1 bg-white"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(createdCredentials.email)}
                    >
                      Kopyala
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-600">Geçici Şifre</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Input
                      value={createdCredentials.temporary_password}
                      readOnly
                      className="flex-1 bg-white font-mono"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(createdCredentials.temporary_password)}
                    >
                      Kopyala
                    </Button>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-800">
                  <p className="font-medium">⚠️ Güvenlik Notu:</p>
                  <p className="mt-1">Kullanıcı ilk girişte şifresini değiştirmek zorundadır.</p>
                </div>
              </div>

              {createdCredentials.whatsapp_link && (
                <div className="space-y-2">
                  <Label className="text-sm text-gray-700">WhatsApp ile Paylaş:</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      value={createdCredentials.whatsapp_link}
                      readOnly
                      className="flex-1 bg-gray-50 text-xs"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(createdCredentials.whatsapp_link)}
                    >
                      Kopyala
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => window.open(createdCredentials.whatsapp_link, '_blank')}
                    >
                      <MessageCircleIcon className="w-4 h-4 mr-1" />
                      WhatsApp'ta Aç
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => {
                    setShowCredentialsModal(false);
                    setCreatedCredentials(null);
                  }}
                  className="btn-primary"
                >
                  Tamam
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Modal */}
      {showDeleteConfirmation && selectedUserToDelete && (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-50" onClick={() => !deletingUser && setShowDeleteConfirmation(false)}></div>
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-[101]">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangleIcon className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Kullanıcıyı Kalıcı Olarak Sil</h3>
                  <p className="text-sm text-gray-600">{selectedUserToDelete.name}</p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800 font-medium mb-2">⚠️ Bu işlem geri alınamaz!</p>
                  <p className="text-xs text-red-700">Kullanıcı kalıcı olarak silinecek ve kullanıcı verisi arşive taşınacaktır.</p>
                </div>

                {deletionImpact && (
                  <div className="space-y-2">
                    {deletionImpact.warnings && deletionImpact.warnings.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-yellow-800 mb-1">Uyarılar:</p>
                        <ul className="text-xs text-yellow-700 space-y-1">
                          {deletionImpact.warnings.map((warning, index) => (
                            <li key={index}>• {warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {deletionImpact.critical_dependencies && deletionImpact.critical_dependencies.length === 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-800">✓ Bu kullanıcının aktif işlemi yok, güvenle silinebilir.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setSelectedUserToDelete(null);
                    setDeletionImpact(null);
                  }}
                  disabled={deletingUser}
                  className="flex-1"
                >
                  İptal
                </Button>
                <Button
                  onClick={handlePermanentDeleteUser}
                  disabled={deletingUser}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {deletingUser ? (
                    <>
                      <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                      Siliniyor...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Evet, Sil
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive User Modal */}
      {archiveUserDialogOpen && selectedUserToArchive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <AlertTriangleIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">İşten Ayrılma Onayı</h3>
                <p className="text-sm text-gray-500">Kullanıcı arşive taşınacak</p>
              </div>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-orange-800 mb-3">
                <strong>{selectedUserToArchive.full_name}</strong> arşiv kullanıcılar arasına konacak. Emin misiniz?
              </p>
              <ul className="text-sm text-orange-700 space-y-1 list-disc list-inside">
                <li>Bu kişi artık hesabına erişemeyecek</li>
                <li>Tekrar geri aktif edilemeyecek</li>
                <li>İsterseniz pasif kullanıcılar arasına alarak daha sonra yeniden aktif edebilirsiniz</li>
              </ul>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setArchiveUserDialogOpen(false);
                  setSelectedUserToArchive(null);
                }}
                disabled={archivingUser}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                onClick={handleArchiveUser}
                disabled={archivingUser}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                {archivingUser ? (
                  <>
                    <div className="loading-spinner mr-2"></div>
                    Arşivleniyor...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4 mr-2" />
                    Tamam, Arşivle
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementSettings;